// Helpers shared by the expense attachment surfaces (the standalone
// attachments modal and the inline panel on the edit form).

// Extensions an employee is allowed to attach to an expense. Detection is by
// extension: it is what the file picker's accept filter needs anyway, and
// sniffing magic bytes can't tell the OOXML formats apart (docx and xlsx are
// both zip containers). The server enforces the same list in
// server/routes/expenses.js — keep the two in step.
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  // Images. heic/heif matter most: they're what iPhone cameras produce.
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.gif',
  '.bmp',
  '.tif',
  '.tiff',
  '.avif',
  // Documents
  '.pdf',
  '.doc',
  '.docx',
  // Spreadsheets
  '.csv',
  '.xls',
  '.xlsx',
];

// Value for an <input type="file"> accept attribute, so the OS picker filters
// to supported files before the user ever chooses one.
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.join(',');

// Short, human phrasing of the allowlist for error messages.
export const ALLOWED_ATTACHMENT_SUMMARY = 'images, PDF, Word (doc/docx), CSV and Excel (xls/xlsx)';

export const getFileExtension = (fileName) => {
  const normalized = String(fileName || '').trim().toLowerCase();
  const lastDot = normalized.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === normalized.length - 1) return '';
  return normalized.slice(lastDot);
};

export const isAllowedAttachmentName = (fileName) =>
  ALLOWED_ATTACHMENT_EXTENSIONS.includes(getFileExtension(fileName));

// Splits a picked/dropped FileList into the files we can accept and the ones
// we can't, so a bad file in a multi-select doesn't discard the good ones.
export const partitionAllowedFiles = (fileList) => {
  const files = Array.from(fileList || []);
  return {
    allowed: files.filter((file) => isAllowedAttachmentName(file?.name)),
    rejected: files.filter((file) => !isAllowedAttachmentName(file?.name)),
  };
};

export const unsupportedFilesMessage = (rejected) => {
  const names = (rejected || []).map((file) => file?.name).filter(Boolean);
  if (names.length === 0) return '';
  const subject = names.length === 1 ? `"${names[0]}"` : `${names.length} files (${names.join(', ')})`;
  return `Unsupported file type: ${subject}. Allowed types are ${ALLOWED_ATTACHMENT_SUMMARY}.`;
};

export const formatAttachmentDateTime = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatBytes = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const triggerBlobPreview = (blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};

// API errors carry the server's own wording as their message (see buildApiError
// in hooks/useQueries.js), so these only need to override the cases where we
// can say something more useful than the API can — the setup gaps, where naming
// the field to fix saves a round of questions.
export const getFriendlyUploadError = (error) => {
  if (error?.code === 'document_library_not_configured') {
    return "This employee's document library isn't set up, so there is nowhere to store the receipt. Ask an admin to set Document Library on the employee profile.";
  }

  if (error?.code === 'sharepoint_site_not_configured') {
    return 'The SharePoint site where receipts are stored is not configured. Ask an admin to check the server settings.';
  }

  return String(error?.message || error || 'Upload failed');
};

export const getFriendlyDeleteError = (error) =>
  String(error?.message || error || 'Delete failed');
